import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { readNarrativeTopics } from '@api/narratives';
import { getTopicLibraryCardIcon } from '../../utilities/helpers';

import {
  ButtonV2,
  CloseIcon,
  HelpPlainIcon,
  IconButtonV2,
  Input,
  LoaderSkeleton,
  PlusPlainIcon,
  toast,
  TopicLibraryCard,
  truncateTextWithEllipsis,
} from '@utilities';

import styles from './_index.module.scss';

const NarrativesTopicsWindow = ({
  onCloseToolWindow,
  onToggleTopicCard,
  selectedTopicIds = [],
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [narrativeTopics, setNarrativeTopics] = useState([]);
  const [searchValue, setSearchValue] = useState('');

  const selectedTopicIdSet = useMemo(() => new Set(selectedTopicIds), [selectedTopicIds]);

  const handleToggleTopic = (topic) => {
    if (!topic?.id || typeof onToggleTopicCard !== 'function') return;
    onToggleTopicCard(topic, !selectedTopicIdSet.has(topic.id));
  };

  useEffect(() => {
    const controller = new AbortController();

    const fetchNarrativeTopics = async () => {
      try {
        const topics = await readNarrativeTopics({ signal: controller.signal });
        setNarrativeTopics(topics);
      } catch (error) {
        if (error.name === 'CanceledError' || error.name === 'AbortError') return;
        console.error('Error fetching narrative topics:', error);
        toast.error('Error loading topics. Please refresh the page or try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchNarrativeTopics();

    return () => {
      controller.abort();
    };
  }, []);

  const normalizedSearchValue = searchValue.trim().toLowerCase();

  const filteredNarrativeTopics = useMemo(
    () =>
      narrativeTopics.filter((topic) =>
        (topic.name?.toLowerCase() || '').includes(normalizedSearchValue)
      ),
    [narrativeTopics, normalizedSearchValue]
  );

  const favoriteTopics = useMemo(
    () => filteredNarrativeTopics.filter((topic) => topic.isFavorited),
    [filteredNarrativeTopics]
  );

  const otherTopics = useMemo(
    () => filteredNarrativeTopics.filter((topic) => !topic.isFavorited),
    [filteredNarrativeTopics]
  );

  const renderLoadingCards = (cardsToRender) =>
    Array.from({ length: cardsToRender }, (_, index) => (
      <TopicLibraryCard key={`loading-card-${index}`}>
        <LoaderSkeleton height={85}>
          <rect height="75" rx="7" ry="7" width="80px" x="0" y="0" />
          <rect height="10" rx="7" ry="15" width="65%" x="110" y="5" />
          <rect height="10" rx="7" ry="15" width="65%" x="110" y="25" />
        </LoaderSkeleton>
      </TopicLibraryCard>
    ));

  const renderTopicCards = (topics) =>
    topics
      .filter((topic) => Boolean(topic?.name))
      .map((topic) => {
        const isActive = selectedTopicIdSet.has(topic.id);

        return (
          <TopicLibraryCard
            footer={
              <div className={styles['topic-card-footer']}>
                <ButtonV2
                  icon={isActive ? <CloseIcon /> : <PlusPlainIcon />}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleToggleTopic(topic);
                  }}
                  size="small"
                  variant="subtle"
                >
                  {isActive ? 'Remove Topic Card' : 'Insert Topic Card'}
                </ButtonV2>

                <IconButtonV2
                  aria-label="Help Center"
                  as="a"
                  href="https://help.numerator.com/en/collections/15743024-narratives"
                  icon={<HelpPlainIcon />}
                  rel="noopener noreferrer"
                  size="small"
                  target="_blank"
                  tooltip="Help Center"
                  tooltipProps={{ placement: 'top' }}
                  variant="subtle"
                />
              </div>
            }
            icon={getTopicLibraryCardIcon(topic.vizType)}
            isActive={isActive}
            key={topic.id}
            title={truncateTextWithEllipsis({ length: 60, text: topic.name }) || 'Untitled Topic'}
          />
        );
      });

  return (
    <div className={styles['topics-window']}>
      <div className={styles['topics-window-header-row']}>
        <strong className={styles['topics-window-header']}>Assemble with Topic Cards</strong>
        {onCloseToolWindow ? (
          <IconButtonV2
            aria-label="Close"
            icon={<CloseIcon />}
            onClick={onCloseToolWindow}
            size="medium"
            tooltip="Close"
            tooltipProps={{ placement: 'left' }}
            variant="default"
          />
        ) : null}
      </div>

      <div className={styles['topics-window-search-input-container']}>
        <Input
          aria-label="Find a topic"
          className={styles['topics-window-search-input']}
          data-testid="narratives-topics-search-input"
          onChange={setSearchValue}
          placeholder="Find a topic"
          value={searchValue}
        />
      </div>

      {isLoading ? (
        <>
          {renderLoadingCards(3)}
          {renderLoadingCards(6)}
        </>
      ) : (
        <>
          {favoriteTopics.length > 0 && (
            <>
              <h4 className={styles['topics-window-section-title']}>Favorite Topic Cards</h4>
              {renderTopicCards(favoriteTopics)}
            </>
          )}

          <h4 className={styles['topics-window-section-title']}>
            {favoriteTopics.length > 0 ? 'Other Topic Cards' : 'Topic Cards'}
          </h4>
          {renderTopicCards(otherTopics)}
        </>
      )}
    </div>
  );
};

NarrativesTopicsWindow.propTypes = {
  onCloseToolWindow: PropTypes.func,
  onToggleTopicCard: PropTypes.func,
  selectedTopicIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
};

export default NarrativesTopicsWindow;
